import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_MS = 2 * 60 * 1000;  // Show warning 2 minutes before timeout

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'click'];

export default function InactivityTimer() {
    const { user, logout } = useAuth();
    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(120);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const clearAllTimers = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
        timerRef.current = null;
        warningTimerRef.current = null;
        countdownRef.current = null;
    }, []);

    const handleLogout = useCallback(() => {
        clearAllTimers();
        setShowWarning(false);
        logout();
    }, [clearAllTimers, logout]);

    const resetTimers = useCallback(() => {
        if (!user) return;
        clearAllTimers();
        setShowWarning(false);

        // Set warning timer
        warningTimerRef.current = setTimeout(() => {
            setShowWarning(true);
            setCountdown(Math.floor(WARNING_MS / 1000));
            countdownRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) return 0;
                    return prev - 1;
                });
            }, 1000);
        }, TIMEOUT_MS - WARNING_MS);

        // Set logout timer
        timerRef.current = setTimeout(handleLogout, TIMEOUT_MS);
    }, [user, clearAllTimers, handleLogout]);

    useEffect(() => {
        if (!user) {
            clearAllTimers();
            setShowWarning(false);
            return;
        }

        resetTimers();

        const handleActivity = () => {
            if (showWarning) return; // Don't reset if warning is showing — user must click
            resetTimers();
        };

        ACTIVITY_EVENTS.forEach(event =>
            document.addEventListener(event, handleActivity, { passive: true })
        );

        return () => {
            clearAllTimers();
            ACTIVITY_EVENTS.forEach(event =>
                document.removeEventListener(event, handleActivity)
            );
        };
    }, [user, resetTimers, clearAllTimers, showWarning]);

    const handleStayLoggedIn = () => {
        setShowWarning(false);
        resetTimers();
    };

    if (!showWarning || !user) return null;

    return (
        <div
            data-testid="inactivity-warning-overlay"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
            }}
        >
            <div
                data-testid="inactivity-warning-dialog"
                style={{
                    background: '#1a1a2e',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '12px',
                    padding: '32px',
                    maxWidth: '420px',
                    width: '90%',
                    textAlign: 'center',
                    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.5)',
                }}
            >
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                </div>
                <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: 600, margin: '0 0 8px' }}>
                    Session Expiring Soon
                </h2>
                <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 20px', lineHeight: 1.5 }}>
                    For HIPAA compliance, your session will expire due to inactivity.
                    You will be logged out in <strong style={{ color: '#f59e0b' }}>{countdown} seconds</strong>.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <button
                        data-testid="stay-logged-in-btn"
                        onClick={handleStayLoggedIn}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#8b5cf6',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                    >
                        Stay Logged In
                    </button>
                    <button
                        data-testid="logout-now-btn"
                        onClick={handleLogout}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '8px',
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            background: 'transparent',
                            color: '#ef4444',
                            fontWeight: 600,
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                    >
                        Logout Now
                    </button>
                </div>
            </div>
        </div>
    );
}
